import type { LayoutBlock } from '@web-studio/shared-schemas';
import { LAYOUT_COMPONENT_TYPE, REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet, canonicalizeMediaUrl, toPublicUploadPath } from '../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { pickBlockField } from '../lib/localized';
import { isDrinkLikeMenuCategoryTitle } from '../lib/menuCategoryKind';
import {
  parseMenuColumns,
  type MenuColumn,
  type MenuItem,
} from '../lib/parseMenuColumns';
import { LUCIDE_SSR_FILE_TEXT, resolveSectionSsrIcon } from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml } from '../icons/renderIconHtml';
import { escapeHtml } from './escape';
import {
  renderMenuItemCodedRowHtml,
  renderMenuItemDietaryTagsHtml,
  renderMenuItemNameHtml,
  renderMenuLegalLegendHtml,
} from './menuItemMarksHtml';
import { HIGHLIGHT_MENU_SSR_OVERRIDES } from './menuHighlightHtmlRenderers';
import type { BlockHtmlRenderContext } from './renderSiteHtml';
import { pickChrome } from '../lib/siteChromeCopy';
import { MENU_FLOW_CHIP_SCROLL_THRESHOLD } from '../blocks/menu/fullMenuDietary';

type MenuHtmlRenderer = (block: LayoutBlock, ctx: BlockHtmlRenderContext) => string;

function columnImageUrl(block: LayoutBlock, column: MenuColumn, index: number): string | undefined {
  const fromColumn = column.imageUrl?.trim();
  if (fromColumn) {
    return fromColumn;
  }
  const fromMedia = block.media[`categoryImage${index}`];
  return typeof fromMedia === 'string' && fromMedia.length > 0 ? fromMedia : undefined;
}

function itemImageUrl(
  block: LayoutBlock,
  item: MenuItem,
  columnIndex: number,
  itemIndex: number,
): string | undefined {
  const fromItem = item.imageUrl?.trim();
  if (fromItem) {
    return fromItem;
  }
  const fromMedia = block.media[`itemImage_${columnIndex}_${itemIndex}`];
  return typeof fromMedia === 'string' && fromMedia.length > 0 ? fromMedia : undefined;
}

function renderMenuPdfCard(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const pdfCta = pickBlockField(block.content, 'pdfCta', locale);
  const pdfCtaIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'pdfCtaIcon', locale),
    LUCIDE_SSR_FILE_TEXT,
  );
  const menuPdfRaw = typeof block.media.menuPdf === 'string' ? block.media.menuPdf.trim() : '';
  const menuPdfCanonical = menuPdfRaw ? canonicalizeMediaUrl(menuPdfRaw) : '';
  const menuPdf = toPublicUploadPath(menuPdfCanonical) ?? menuPdfCanonical;
  const viewerTitle = sectionTitle.trim() || 'Speisekarte PDF';
  const emptyHint =
    locale.toLowerCase().startsWith('de')
      ? 'PDF-URL oder Datei hinterlegen, um die Speisekarte anzuzeigen.'
      : locale.toLowerCase().startsWith('vi')
        ? 'Thêm URL hoặc tệp PDF để hiển thị thực đơn.'
        : 'Add a PDF URL or file to show the menu.';

  const frameHtml = menuPdf
    ? `<iframe class="ws-menu-pdf__viewer" src="${escapeHtml(menuPdf)}" title="${escapeHtml(viewerTitle)}" loading="lazy"></iframe>`
    : `<div class="ws-menu-pdf__empty" role="status">${renderLucideIconHtml(LUCIDE_SSR_FILE_TEXT.iconNode, { size: 'md', name: LUCIDE_SSR_FILE_TEXT.name })}<p class="ws-menu-pdf__empty-text">${escapeHtml(emptyHint)}</p></div>`;

  const ctaIconHtml = renderLucideIconHtml(pdfCtaIcon.iconNode, {
    size: 'sm',
    name: pdfCtaIcon.name,
  });
  const ctaHtml = menuPdf
    ? `<a class="ws-menu-pdf__download" href="${escapeHtml(menuPdf)}" target="_blank" rel="noopener noreferrer">${ctaIconHtml}<span class="ws-menu-pdf__download-label">${escapeHtml(pdfCta)}</span></a>`
    : '';

  return `<section class="ws-menu-pdf" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  <div class="ws-menu-pdf__inner">
    <header class="ws-menu-pdf__intro">
      ${sectionTitle ? `<h2 class="ws-menu-pdf__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<p class="ws-menu-pdf__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    <div class="ws-menu-pdf__viewer-shell">${frameHtml}${ctaHtml}</div>
  </div>
</section>`;
}

function renderMenuCategoryGallery(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const bands = columns
    .map((col, columnIndex) => {
      const imageUrl = columnImageUrl(block, col, columnIndex);
      const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 900, 78) : '';
      const imageSrcSet = imageUrl
        ? buildSiteImageSrcSet(imageUrl, [400, 640, 900, 1200], 78)
        : '';
      const visual = imageUrl
        ? `<img class="ws-menu-gallery__visual-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuCategory)}" alt="${escapeHtml(col.title || 'Category')}" width="900" height="720" loading="lazy" decoding="async" />`
        : '<div class="ws-menu-gallery__visual-placeholder" aria-hidden="true"></div>';
      const items = (col.items ?? [])
        .map((item) => {
          const dietary = renderMenuItemDietaryTagsHtml(item, locale);
          const nameHtml = renderMenuItemNameHtml(item, locale, {
            nameClass: 'ws-menu__item-name ws-menu-gallery__item-name',
            showOrderCode: false,
          });
          const descHtml = item.description
            ? `<p class="ws-menu-gallery__item-desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<article class="ws-menu-gallery__item">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `${nameHtml}${descHtml}${dietary}`,
            codeClass: 'ws-menu-gallery__item-code',
            priceClass: 'ws-menu-gallery__item-price',
            rowClass: 'ws-menu-gallery__item-row',
            copyClass: 'ws-menu-gallery__item-copy',
          })}</article>`;
        })
        .join('');
      const flip = columnIndex % 2 === 1 ? ' ws-menu-gallery__band--flip' : '';
      return `<div class="ws-menu-gallery__band${flip}"><div class="ws-menu-gallery__visual">${visual}${col.title ? `<h3 class="ws-menu-gallery__category">${escapeHtml(col.title)}</h3>` : ''}</div><div class="ws-menu-gallery__list">${items}</div></div>`;
    })
    .join('');

  return `<section class="ws-menu-gallery" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  <div class="ws-menu-gallery__inner">
    <header class="ws-menu-gallery__intro">
      ${sectionTitle ? `<h2 class="ws-menu-gallery__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<p class="ws-menu-gallery__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    <div class="ws-menu-gallery__bands">${bands}</div>
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderMenuDishCards(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const categories = columns
    .map((col, columnIndex) => {
      const cards = (col.items ?? [])
        .map((item, itemIndex) => {
          const imageUrl = itemImageUrl(block, item, columnIndex, itemIndex);
          const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : '';
          const imageSrcSet = imageUrl
            ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900], 78)
            : '';
          const media = imageUrl
            ? `<img class="ws-menu-dishes__media-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuDishCard)}" alt="${escapeHtml(item.name || 'Dish')}" width="720" height="720" loading="lazy" decoding="async" />`
            : '<div class="ws-menu-dishes__media-placeholder" aria-hidden="true"></div>';
          const dietary = renderMenuItemDietaryTagsHtml(item, locale, {
            iconsOnly: true,
            placement: 'inline',
          });
          const name = renderMenuItemNameHtml(item, locale, {
            nameClass: 'ws-menu-dishes__name',
            showOrderCode: false,
          });
          const desc = item.description
            ? `<p class="ws-menu-dishes__desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<article class="ws-menu-dishes__card"><div class="ws-menu-dishes__media">${media}</div><div class="ws-menu-dishes__body">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `<div class="ws-menu-dishes__primary">${name}${dietary}</div>${desc}`,
            codeClass: 'ws-menu-dishes__code',
            priceClass: 'ws-menu-dishes__price',
            rowClass: 'ws-menu-dishes__row',
            copyClass: 'ws-menu-dishes__copy',
          })}</div></article>`;
        })
        .join('');
      return `<div class="ws-menu-dishes__category">${col.title ? `<h3 class="ws-menu-dishes__category-title">${escapeHtml(col.title)}</h3>` : ''}<div class="ws-menu-dishes__grid">${cards}</div></div>`;
    })
    .join('');

  return `<section class="ws-menu-dishes" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  <div class="ws-menu-dishes__inner">
    <header class="ws-menu-dishes__intro">
      ${sectionTitle ? `<h2 class="ws-menu-dishes__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<p class="ws-menu-dishes__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    ${categories}
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderMenuEditorialBoard(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const board = columns
    .map((col) => {
      const items = (col.items ?? [])
        .map((item) => {
          const dietary = renderMenuItemDietaryTagsHtml(item, locale);
          const name = renderMenuItemNameHtml(item, locale, {
            nameClass: 'ws-menu-editorial__name',
            showOrderCode: false,
          });
          const desc = item.description
            ? `<p class="ws-menu-editorial__desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<article class="ws-menu-editorial__item">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `<div class="ws-menu-editorial__row">${name}<span class="ws-menu-editorial__leader" aria-hidden="true"></span></div>${desc}${dietary}`,
            codeClass: 'ws-menu-editorial__code',
            priceClass: 'ws-menu-editorial__price',
            rowClass: 'ws-menu-editorial__layout',
            copyClass: 'ws-menu-editorial__main',
          })}</article>`;
        })
        .join('');
      return `<div class="ws-menu-editorial__section">${col.title ? `<h3 class="ws-menu-editorial__section-title">${escapeHtml(col.title)}</h3>` : ''}${items}</div>`;
    })
    .join('');

  return `<section class="ws-menu-editorial" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  <div class="ws-menu-editorial__inner">
    <header class="ws-menu-editorial__intro">
      ${sectionTitle ? `<h2 class="ws-menu-editorial__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      <div class="ws-menu-editorial__rule" aria-hidden="true"></div>
      ${sectionSubtitle ? `<p class="ws-menu-editorial__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    <div class="ws-menu-editorial__board">${board}${renderMenuLegalLegendHtml(columns, locale)}</div>
  </div>
</section>`;
}

function renderMenuStreetBoard(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const groupName = `menu-street-${block.blockId}`;
  const boardLabel = escapeHtml(sectionTitle || pickChrome('menuCategories', locale));
  const countLabel = escapeHtml(pickChrome('menuItemCount', locale));

  const chrome = columns
    .map((col, columnIndex) => {
      const imageUrl = columnImageUrl(block, col, columnIndex);
      const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : '';
      const imageSrcSet = imageUrl
        ? buildSiteImageSrcSet(imageUrl, [320, 480, 720, 960], 78)
        : '';
      const title = col.title.trim() || `Category ${columnIndex + 1}`;
      const count = (col.items ?? []).filter(
        (item) => item.name.trim() || item.price.trim() || item.description.trim(),
      ).length;
      const visual = imageUrl
        ? `<img class="ws-menu-street__tile-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuCategory)}" alt="" width="720" height="540" loading="lazy" decoding="async" />`
        : '<span class="ws-menu-street__tile-placeholder" aria-hidden="true"></span>';
      const items = (col.items ?? [])
        .map((item) => {
          if (!item.name.trim() && !item.price.trim() && !item.description.trim()) {
            return '';
          }
          const dietary = renderMenuItemDietaryTagsHtml(item, locale, {
            iconsOnly: true,
            placement: 'inline',
          });
          const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
          const desc = item.description
            ? `<p class="ws-menu-street__item-desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<li><article class="ws-menu-street__item">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `<div class="ws-menu__item-primary">${name}${dietary}</div>${desc}`,
            codeClass: 'ws-menu-street__item-code',
            priceClass: 'ws-menu-street__item-price',
            rowClass: 'ws-menu-street__item-row',
            copyClass: 'ws-menu-street__item-copy',
          })}</article></li>`;
        })
        .join('');
      const checked = columnIndex === 0 ? ' checked' : '';
      return `<input type="radio" name="${escapeHtml(groupName)}" id="${escapeHtml(groupName)}-tab-${columnIndex}" class="ws-menu-street__radio"${checked} />
<label id="${escapeHtml(groupName)}-label-${columnIndex}" for="${escapeHtml(groupName)}-tab-${columnIndex}" class="ws-menu-street__tile"><span class="ws-menu-street__tile-visual">${visual}</span><span class="ws-menu-street__tile-meta"><span class="ws-menu-street__tile-title">${escapeHtml(title)}</span>${count > 0 ? `<span class="ws-menu-street__tile-count">${count} ${countLabel}</span>` : ''}</span></label>
<div class="ws-menu-street__panel" id="${escapeHtml(groupName)}-panel-${columnIndex}" role="region" aria-labelledby="${escapeHtml(groupName)}-label-${columnIndex}"><h3 class="ws-menu-street__panel-title">${escapeHtml(title)}</h3><ul class="ws-menu-street__list">${items}</ul></div>`;
    })
    .join('');

  return `<section class="ws-menu-street" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  <div class="ws-menu-street__inner">
    <header class="ws-menu-street__intro">
      ${sectionTitle ? `<h2 class="ws-menu-street__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<p class="ws-menu-street__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    ${columns.length > 0 ? `<div class="ws-menu-street__chrome" role="radiogroup" aria-label="${boardLabel}">${chrome}</div>` : ''}
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderMenuCategoryChips(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, allBlocks } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const stripDrinks = Boolean(
    allBlocks?.some((entry) => entry.componentType === LAYOUT_COMPONENT_TYPE.drinksListStatic),
  );
  const columns = parseMenuColumns(menuDataRaw).filter(
    (column) => !stripDrinks || !isDrinkLikeMenuCategoryTitle(column.title),
  );
  const groupName = `menu-flow-${block.blockId}`;
  const chromeId = `${groupName}-chrome`;
  const categoriesLabel = escapeHtml(sectionTitle || pickChrome('menuCategories', locale));
  const chipsOverflow = columns.length > MENU_FLOW_CHIP_SCROLL_THRESHOLD;
  const ruleHtml =
    sectionTitle || sectionSubtitle ? '<div class="ws-menu-flow__rule" aria-hidden="true"></div>' : '';

  const panelShowCss = columns
    .map(
      (_, index) =>
        `#${chromeId}:has(#${groupName}-tab-${index}:checked) #${groupName}-panel-${index}{display:block}`,
    )
    .join('');

  const tabs = columns
    .map((col, index) => {
      const title = escapeHtml(col.title.trim() || `Category ${index + 1}`);
      const checked = index === 0 ? ' checked' : '';
      return `<span class="ws-menu-flow__tab-wrap"><input type="radio" name="${escapeHtml(groupName)}" id="${escapeHtml(groupName)}-tab-${index}" class="ws-menu-flow__radio"${checked} /><label class="ws-menu-flow__tab" id="${escapeHtml(groupName)}-label-${index}" for="${escapeHtml(groupName)}-tab-${index}">${title}</label></span>`;
    })
    .join('');

  const panels = columns
    .map((col, index) => {
      const items = (col.items ?? [])
        .map((item) => {
          if (!item.name.trim() && !item.price.trim() && !item.description.trim()) {
            return '';
          }
          const dietary = renderMenuItemDietaryTagsHtml(item, locale, {
            iconsOnly: true,
            placement: 'inline',
          });
          const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
          const desc = item.description
            ? `<p class="ws-menu__item-desc">${escapeHtml(item.description)}</p>`
            : '';
          return `<li><article class="ws-menu__item">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `<div class="ws-menu__item-primary">${name}${dietary}</div>${desc}`,
          })}</article></li>`;
        })
        .join('');
      return `<div class="ws-menu-flow__panel" id="${escapeHtml(groupName)}-panel-${index}" data-panel="${index}" role="region" aria-labelledby="${escapeHtml(groupName)}-label-${index}"><ul class="ws-menu-flow__list">${items}</ul></div>`;
    })
    .join('');

  const overflowAttr = chipsOverflow ? ' data-chips-overflow="true"' : '';

  return `<section class="ws-menu-flow" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">
  ${panelShowCss ? `<style>${panelShowCss}</style>` : ''}
  <div class="ws-menu-flow__intro">
    ${sectionTitle ? `<h2 class="ws-menu-flow__title">${escapeHtml(sectionTitle)}</h2>` : ''}
    ${sectionSubtitle ? `<span class="ws-menu-flow__subtitle">${escapeHtml(sectionSubtitle)}</span>` : ''}
    ${ruleHtml}
  </div>
  <div class="ws-menu-flow__body">
    ${
      columns.length > 0
        ? `<div class="ws-menu-flow__chrome" id="${escapeHtml(chromeId)}"><div class="ws-menu-flow__tabs" role="radiogroup" aria-label="${categoriesLabel}" data-chip-count="${columns.length}"${overflowAttr}><div class="ws-menu-flow__rail">${tabs}</div></div><div class="ws-menu-flow__panels">${panels}</div></div>`
        : ''
    }
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderMenuHighlightStrip(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const plates: string[] = [];
  let railIndex = 0;
  for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
    const col = columns[columnIndex]!;
    for (let itemIndex = 0; itemIndex < (col.items ?? []).length; itemIndex += 1) {
      const item = col.items![itemIndex]!;
      if (!item.name?.trim() && !item.price?.trim() && !item.description?.trim()) {
        continue;
      }
      const imageUrl = itemImageUrl(block, item, columnIndex, itemIndex);
      const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 900, 78) : '';
      const imageSrcSet = imageUrl
        ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900, 1200], 78)
        : '';
      const media = imageUrl
        ? `<img class="ws-menu-highlight__media-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuHighlightPlate)}" alt="${escapeHtml(item.name || 'Dish')}" width="900" height="1125" loading="${railIndex < 2 ? 'eager' : 'lazy'}" decoding="async" />`
        : `<div class="ws-menu-highlight__media-fallback" aria-hidden="true"><span class="ws-menu-highlight__media-glyph">${escapeHtml((item.name.trim().charAt(0) || '·').toUpperCase())}</span></div>`;
      const lead = railIndex === 0 ? ' ws-menu-highlight__plate--lead' : '';
      plates.push(
        `<article class="ws-menu-highlight__plate${lead}" role="listitem" style="--ws-highlight-i: ${railIndex}"><div class="ws-menu-highlight__media">${media}</div><div class="ws-menu-highlight__caption"><div class="ws-menu-highlight__head">${renderMenuItemNameHtml(item, locale, { nameClass: 'ws-menu-highlight__name', showLegalMarks: false, showOrderCode: false })}${renderMenuItemDietaryTagsHtml(item, locale, { iconsOnly: true, placement: 'inline' })}</div>${item.description ? `<p class="ws-menu-highlight__desc">${escapeHtml(item.description)}</p>` : ''}</div></article>`,
      );
      railIndex += 1;
    }
  }

  const marksNavLabel = pickChrome('highlightPromenadeNav', locale);
  const goToDishLabel = pickChrome('highlightPromenadeGoTo', locale);
  const marks =
    plates.length > 1
      ? `<nav class="ws-menu-highlight__marks" aria-label="${escapeHtml(marksNavLabel)}">${plates
          .map(
            (_plate, index) =>
              `<button type="button" class="ws-menu-highlight__mark" aria-label="${escapeHtml(goToDishLabel.replace('{n}', String(index + 1)))}"${index === 0 ? ' aria-current="true"' : ''}></button>`,
          )
          .join('')}</nav>`
      : '';

  return `<section class="ws-menu-highlight" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" data-ws-hl-promenade id="menu">
  <div class="ws-menu-highlight__stage">
    <header class="ws-menu-highlight__intro">
      <span class="ws-menu-highlight__accent" aria-hidden="true"></span>
      ${sectionTitle ? `<h2 class="ws-menu-highlight__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<p class="ws-menu-highlight__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    <div class="ws-menu-highlight__rail-wrap" data-overflow="${plates.length > 1 ? 'true' : 'false'}">
      <div class="ws-menu-highlight__rail-shell">
        <div class="ws-menu-highlight__rail" role="list" aria-label="${escapeHtml(sectionTitle || 'Highlights')}"${plates.length > 1 ? ' tabindex="0"' : ''}>
          ${plates.join('')}
        </div>
      </div>
      ${marks}
    </div>
  </div>
</section>`;
}

function renderDrinksListCellar(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const regions = columns
    .map((col) => {
      const bins = (col.items ?? [])
        .filter((item) => item.name.trim() || item.price.trim())
        .map((item) => {
          const dietary = renderMenuItemDietaryTagsHtml(item, locale);
          const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
          const desc = item.description
            ? `<p class="ws-drinks-cellar__origin">${escapeHtml(item.description)}</p>`
            : '';
          return `<li class="ws-drinks-cellar__bin">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `${name}${desc}${dietary}`,
            priceClass: 'ws-drinks-cellar__glass',
          })}</li>`;
        })
        .join('');
      return `<div class="ws-drinks-cellar__region">${col.title.trim() ? `<h3 class="ws-drinks-cellar__region-title">${escapeHtml(col.title)}</h3>` : ''}<ul class="ws-drinks-cellar__bins">${bins}</ul></div>`;
    })
    .join('');

  return `<section class="ws-drinks-cellar" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="drinks">
  <div class="ws-drinks-cellar__inner">
    <header class="ws-drinks-cellar__intro">
      ${sectionTitle ? `<h2 class="ws-drinks-cellar__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<span class="ws-drinks-cellar__subtitle">${escapeHtml(sectionSubtitle)}</span>` : ''}
    </header>
    <div class="ws-drinks-cellar__book">${regions}</div>
    ${renderMenuLegalLegendHtml(columns, locale)}
  </div>
</section>`;
}

function renderDrinksListSheet(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const regions = columns
    .map((col) => {
      const bins = (col.items ?? [])
        .filter((item) => item.name.trim() || item.price.trim())
        .map((item) => {
          const dietary = renderMenuItemDietaryTagsHtml(item, locale);
          const name = renderMenuItemNameHtml(item, locale, { showOrderCode: false });
          const desc = item.description
            ? `<p class="ws-drinks-sheet__origin">${escapeHtml(item.description)}</p>`
            : '';
          return `<li class="ws-drinks-sheet__bin">${renderMenuItemCodedRowHtml({
            code: item.code,
            price: item.price,
            copyHtml: `${name}${desc}${dietary}`,
            priceClass: 'ws-drinks-sheet__glass',
          })}</li>`;
        })
        .join('');
      return `<div class="ws-drinks-sheet__region">${col.title.trim() ? `<h3 class="ws-drinks-sheet__region-title">${escapeHtml(col.title)}</h3>` : ''}<ul class="ws-drinks-sheet__bins">${bins}</ul></div>`;
    })
    .join('');

  return `<section class="ws-drinks-sheet" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="drinks">
  <div class="ws-drinks-sheet__inner">
    <header class="ws-drinks-sheet__intro">
      ${sectionTitle ? `<h2 class="ws-drinks-sheet__title">${escapeHtml(sectionTitle)}</h2>` : ''}
      <div class="ws-drinks-sheet__rule" aria-hidden="true"></div>
      ${sectionSubtitle ? `<p class="ws-drinks-sheet__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    <div class="ws-drinks-sheet__card">${regions}${renderMenuLegalLegendHtml(columns, locale)}</div>
  </div>
</section>`;
}

const P = REGISTRY_BLOCK.presentation;

export const MENU_SSR_REGISTRY_OVERRIDES: Partial<Record<string, MenuHtmlRenderer>> = {
  [P.staticMenuPdfCard]: renderMenuPdfCard,
  [P.staticMenuCategoryGallery]: renderMenuCategoryGallery,
  [P.staticMenuDishCards]: renderMenuDishCards,
  [P.staticMenuEditorialBoard]: renderMenuEditorialBoard,
  [P.staticMenuStreetBoard]: renderMenuStreetBoard,
  [P.staticMenuCategoryChips]: renderMenuCategoryChips,
  [P.staticMenuHighlightStrip]: renderMenuHighlightStrip,
  [P.drinksListCellar]: renderDrinksListCellar,
  [P.drinksListSheet]: renderDrinksListSheet,
  ...HIGHLIGHT_MENU_SSR_OVERRIDES,
};
